import React, { useState, useMemo } from 'react';
import ToolShell, { ACCENT, INK, fmt, roundTo, Field, input, card } from '../tools/ToolShell';

/*
 * PUBLIC, no-login lead-magnet tool: /tools/brand-deal-price-calculator
 * Targets high-intent India queries like "how much to charge brands per 100k
 * subscribers" / "sponsored post price india". Creator enters reach + deliverable
 * + usage rights and gets a recommended price RANGE for ONE deliverable.
 *
 * NO-CANNIBALIZE RULE: this is single-shot & stateless — it prices one deal and
 * stops. Saving rates, building the full rate-card, sending the pitch/contract/
 * invoice and tracking payment all live in the app. (See SEO_TOOLS.md + the
 * tools-no-cannibalize design rule.)
 *
 * Built entirely from <ToolShell> — this file is ONLY config + math + two cards.
 */

// Base influencer rate in ₹ per 1,000 *views* for one deliverable (India 2025
// benchmarks for a single sponsored integration, organic-only, before niche /
// engagement / usage multipliers). Per-view pricing is the credible driver —
// brands buy attention, not raw follower counts.
const DELIVERABLES = {
  YouTube: {
    'Dedicated video': 500,
    'Integration (60–90s)': 200,
    'Short': 100,
    viewRate: 0.10,        // fallback avg views ≈ 10% of subscribers
    audienceWord: 'subscribers',
  },
  Instagram: {
    'Reel': 220,
    'Story (per frame)': 60,
    'Static post / Carousel': 150,
    viewRate: 0.30,        // fallback Reel views ≈ 30% of followers
    audienceWord: 'followers',
  },
};
const PLATFORMS = Object.keys(DELIVERABLES);

// Brand budgets skew by niche — finance/tech command far higher CPMs than
// entertainment. Multiplier on the base per-view rate.
const NICHE_MULT = {
  'Finance / Business': 1.4,
  'Tech / Gadgets': 1.25,
  'Education': 1.1,
  'Beauty / Fashion': 1.15,
  'Fitness / Health': 1.0,
  'Food / Cooking': 0.95,
  'Travel': 1.0,
  'Lifestyle / Vlog': 0.9,
  'Gaming': 0.9,
  'Comedy / Entertainment': 0.85,
  'Other': 1.0,
};
const NICHES = Object.keys(NICHE_MULT);

// How the brand wants to USE the content — the single biggest price lever after
// reach. Paid-ad / whitelisting rights are worth far more than organic-only.
const USAGE = {
  'Organic post only': 1.0,
  'Brand may repost organically': 1.15,
  'Paid ads / whitelisting (3 mo)': 1.5,
  'Paid ads / whitelisting (6 mo)': 1.8,
};
const USAGE_OPTS = Object.keys(USAGE);

const EXCLUSIVITY = {
  'None': 1.0,
  'Category exclusivity — 1 month': 1.15,
  'Category exclusivity — 3 months': 1.35,
};
const EXCL_OPTS = Object.keys(EXCLUSIVITY);

export default function BrandDealPriceToolPage() {
  const [platform, setPlatform] = useState(PLATFORMS[0]);
  const cfg = DELIVERABLES[platform];
  const deliverableOpts = Object.keys(cfg).filter(k => k !== 'viewRate' && k !== 'audienceWord');

  const [deliverable, setDeliverable] = useState(deliverableOpts[0]);
  const [audience, setAudience] = useState('');     // subs / followers
  const [avgViews, setAvgViews] = useState('');     // optional override
  const [niche, setNiche] = useState(NICHES[0]);
  const [usage, setUsage] = useState(USAGE_OPTS[0]);
  const [exclusivity, setExclusivity] = useState(EXCL_OPTS[0]);

  // Keep deliverable valid when platform changes.
  const onPlatform = (p) => {
    setPlatform(p);
    const opts = Object.keys(DELIVERABLES[p]).filter(k => k !== 'viewRate' && k !== 'audienceWord');
    setDeliverable(opts[0]);
  };

  const result = useMemo(() => {
    const aud = parseInt(audience, 10) || 0;
    const ratePerK = cfg[deliverable];
    if (!ratePerK) return null;

    // Expected views: use the creator's real average if given, else estimate
    // from audience size × the platform's typical view-rate.
    const typedViews = parseInt(avgViews, 10) || 0;
    const views = typedViews > 0 ? typedViews : Math.round(aud * cfg.viewRate);
    if (views <= 0) return null;

    const mult = (NICHE_MULT[niche] || 1) * USAGE[usage] * EXCLUSIVITY[exclusivity];
    const mid = (views / 1000) * ratePerK * mult;
    if (mid <= 0) return null;

    const step = mid >= 50000 ? 2500 : mid >= 10000 ? 1000 : 500;
    const low = roundTo(mid * 0.8, step);
    const high = roundTo(mid * 1.2, step);

    // Answer the literal "per 100k {audience}" search query.
    const per100k = aud > 0 ? Math.round((mid / aud) * 100000) : null;

    return { low, high, views, estimated: typedViews <= 0, per100k, audWord: cfg.audienceWord };
  }, [audience, avgViews, deliverable, niche, usage, exclusivity, cfg]);

  return (
    <ToolShell
      seo={{
        title: 'Brand Deal Price Calculator India — How Much to Charge — CreatorKhata',
        description: 'Free brand-deal price calculator for Indian creators. Find out how much to charge brands for a sponsored YouTube video, Instagram Reel or post — based on your reach, niche and usage rights. No signup.',
        path: '/tools/brand-deal-price-calculator',
      }}
      h1="Brand Deal Price Calculator (India)"
      sub="How much should you charge brands for a sponsored post? Enter your reach, deliverable and usage rights to get a fair price range for one deal — no signup."
      cta={{
        heading: 'Knowing the price is step one. Getting paid is the rest.',
        body: 'CreatorKhata turns the number into money — store your rates, send the pitch, generate the contract & GST invoice, and track every payment till it lands. One deal or fifty, nothing slips.',
      }}
    >
      {/* Inputs */}
      <div style={card()}>
        <Field label="Platform">
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {PLATFORMS.map(p => (
              <button key={p} onClick={() => onPlatform(p)} style={{
                flex: 1, padding: '0.55rem', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: '0.8rem',
                border: `1.5px solid ${platform === p ? ACCENT : '#e0e0e0'}`,
                background: platform === p ? ACCENT : 'white', color: platform === p ? 'white' : '#666',
              }}>{p}</button>
            ))}
          </div>
        </Field>

        <Field label="Deliverable">
          <select style={input} value={deliverable} onChange={e => setDeliverable(e.target.value)}>
            {deliverableOpts.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </Field>

        <Field label={`Your ${cfg.audienceWord}`}>
          <input style={input} type="number" value={audience} onChange={e => setAudience(e.target.value)} placeholder="e.g. 100000" />
        </Field>

        <Field label="Average views per post (optional — more accurate)">
          <input style={input} type="number" value={avgViews} onChange={e => setAvgViews(e.target.value)} placeholder={`leave blank to estimate from ${cfg.audienceWord}`} />
        </Field>

        <Field label="Niche">
          <select style={input} value={niche} onChange={e => setNiche(e.target.value)}>
            {NICHES.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </Field>

        <Field label="Usage rights">
          <select style={input} value={usage} onChange={e => setUsage(e.target.value)}>
            {USAGE_OPTS.map(u => <option key={u} value={u}>{u}</option>)}
          </select>
        </Field>

        <Field label="Exclusivity">
          <select style={input} value={exclusivity} onChange={e => setExclusivity(e.target.value)}>
            {EXCL_OPTS.map(x => <option key={x} value={x}>{x}</option>)}
          </select>
        </Field>

        <p style={{ fontSize: '0.72rem', color: '#aaa', margin: '0.25rem 0 0', lineHeight: 1.5 }}>
          Priced on <b>expected views</b> (what brands actually pay for), adjusted for niche, how the brand may reuse the content, and exclusivity. India 2025 benchmarks — a starting point for your negotiation, not a fixed quote.
        </p>
      </div>

      {/* Output */}
      <div style={card()}>
        {!result ? (
          <div style={{ textAlign: 'center', color: '#aaa', padding: '3rem 1rem' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>🤝</div>
            Enter your <b style={{ color: ACCENT }}>{cfg.audienceWord}</b> on the left — your recommended price appears here <b>instantly</b>. No submit button.
          </div>
        ) : (
          <div>
            <div style={{ borderBottom: `3px solid ${ACCENT}`, paddingBottom: '0.75rem', marginBottom: '0.75rem' }}>
              <div style={{ color: '#888', fontSize: '0.8rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Recommended price · {deliverable}
              </div>
              <div style={{ fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: '1.9rem', color: ACCENT }}>
                {fmt(result.low)} – {fmt(result.high)}
              </div>
              <div style={{ color: '#999', fontSize: '0.8rem' }}>
                based on {result.estimated ? '~' : ''}{Number(result.views).toLocaleString('en-IN')} expected views{result.estimated ? ' (estimated)' : ''}
              </div>
            </div>

            {result.per100k != null && (
              <Stat label={`≈ Rate per 100k ${result.audWord}`} value={fmt(result.per100k)} accent />
            )}
            <Stat label="Usage rights applied" value={usage} />
            <Stat label="Exclusivity" value={exclusivity} />

            <div style={{ marginTop: '0.9rem', background: '#f1f8f4', border: `1px solid ${ACCENT}22`, borderRadius: 10, padding: '0.85rem 1rem', fontSize: '0.82rem', color: '#2a5d42' }}>
              💡 This is for <b>one deliverable</b>. Bundling several (e.g. video + Shorts + Stories) usually earns more than pricing each alone — and <b>always</b> charge extra when the brand runs your content as paid ads.
            </div>

            <p style={{ fontSize: '0.72rem', color: '#aaa', marginTop: '0.75rem' }}>
              Indicative range (2025 India benchmarks). Real rates vary with engagement quality, audience location, turnaround and how badly the brand wants you.
            </p>
          </div>
        )}
      </div>
    </ToolShell>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '0.6rem 0', borderBottom: '1px solid #eee' }}>
      <span style={{ fontSize: '0.85rem', color: '#555', fontWeight: 600 }}>{label}</span>
      <span style={{ fontWeight: 700, fontSize: '0.95rem', color: accent ? ACCENT : INK }}>{value}</span>
    </div>
  );
}
